"use client";

import React, { useState, useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { PageNavigation } from "@/components/layout/PageNavigation";
import { useLeaderboard } from "fansunited-data-layer/client";
import { AdSlot } from "@/features/ads/AdSlot";
import { useLabels } from "@/lib/labels/LabelsProvider";

// Dynamically import tabs — only the active tab's client JS is loaded
const PlayTab = dynamic(() => import("../tabs/play/PlayTab").then((m) => ({ default: m.PlayTab })));
const PrivateLeaguesTab = dynamic(() =>
    import("../tabs/PrivateLeaguesTab").then((m) => ({ default: m.PrivateLeaguesTab }))
);
const LeaderboardTab = dynamic(() => import("../tabs/LeaderboardTab").then((m) => ({ default: m.LeaderboardTab })));
const RulesTab = dynamic(() => import("../tabs/RulesTab").then((m) => ({ default: m.RulesTab })));
const PrizesTab = dynamic(() => import("../tabs/PrizesTab").then((m) => ({ default: m.PrizesTab })));

type Tab = "play" | "private-leagues" | "leaderboard" | "rules" | "prizes";

export function Predictor({
    headerColors,
    breadcrumbs,
}: {
    headerColors?: ResolvedHeaderColors;
    breadcrumbs?: React.ReactNode;
}) {
    const [activeTab, setActiveTab] = useState<Tab>("play");
    const { template } = useLeaderboard();
    const labels = useLabels().predictorPage;

    const tabs = useMemo<{ id: Tab; label: string }[]>(
        () => [
            { id: "play", label: labels.tabs.play },
            { id: "private-leagues", label: labels.tabs.privateLeagues },
            { id: "leaderboard", label: labels.tabs.leaderboard },
            { id: "rules", label: labels.tabs.rules },
            { id: "prizes", label: labels.tabs.prizes },
        ],
        [labels.tabs]
    );

    if (!template) return null;

    // Handle hash-based navigation
    useEffect(() => {
        const hash = window.location.hash.slice(1) as Tab;
        if (hash && tabs.some((tab) => tab.id === hash)) {
            setActiveTab(hash);
        }
    }, [tabs]);

    // Update hash when tab changes
    const handleTabChange = (tabId: Tab) => {
        setActiveTab(tabId);
        window.history.pushState(null, "", `#${tabId}`);
    };

    return (
        <div className="min-h-screen">
            {/* Header */}
            <BrandedHeader
                titleAs="h1"
                title={template.name || labels.title}
                subtitle={template.description?.replace(/<[^>]*>/g, "")}
                imageUrl={template.images?.main ?? undefined}
                colors={headerColors?.colors}
                backgroundImageUrl={headerColors?.backgroundImageUrl}
                breadcrumbs={breadcrumbs}
            />

            {/* Tabs Navigation */}
            <PageNavigation options={tabs} activeId={activeTab} onChange={handleTabChange} sticky />

            {/* Tab Content */}
            <div className="max-w-350 mx-auto px-4 py-8 space-y-8">
                <AdSlot id="content-top" />

                {activeTab === "play" && <PlayTab />}
                {activeTab === "private-leagues" && <PrivateLeaguesTab />}
                {activeTab === "leaderboard" && <LeaderboardTab />}
                {activeTab === "rules" && <RulesTab />}
                {activeTab === "prizes" && <PrizesTab />}

                <AdSlot id="content-bottom" />
            </div>
        </div>
    );
}
